import { EditableSectionContainer, EditableSectionRow, EditableSectionRowTip, useEditableSectionListContext, useEditableSectionsContext, useAlert } from '@uplift/ui-v2';
import { MatchingProfileRow, TreatmentApproach } from '@uplift/types';
import { useProfileContext } from '../profile.context';
import { Box, Typography } from '@mui/material';
import { useProfileStyles } from '../styles';
import { IconWithContent } from '../components/IconWithContent.component';
import { PublicOutlined, SchoolOutlined } from '@mui/icons-material';
import { CommunityExpertiseForm, EducationForm, SpecialtiesForm, TherapueticStyleForm, TreatmentApproachForm, useProfileFormikConfigs } from '../forms';
import { PROFILE_QUERY_KEY, profileRowKeyToLabel } from '../profile.utils';
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteProviderQualificationDegree } from '@uplift/api';
import { FoldedHandsIcon, GroupsIcon } from '@uplift/icons';
import { OptionalTypography } from '../../settings-page/components';
import { DeleteResourceModal } from '../components/DeleteResourceModal.component';
import { RequiredProfileEditableSectionRow, RequiredProfileEditableSectionRowList } from '../components/RequiredProfileRow.component';

const getTreatmentApproachName = (treatmentApproach: TreatmentApproach) => {
  // if treatment approach name has parentheses pair, extract the content
  // source: https://stackoverflow.com/questions/17779744/regular-expression-to-get-a-string-between-parentheses-in-javascript
  const code = /\(([^)]+)\)/.exec(treatmentApproach.name)?.[1];

  // if extracted code exists, return code, otherwise return name
  // ex: "Cognitive Behavioral (CBT)" -> "CBT"
  // ex: "Cognitive Behavioral" -> "Cognitive Behavioral"
  return code ?? treatmentApproach.name;
};

export const ClinicalExperienceSection: React.FC = () => {
  const { classes } = useProfileStyles();
  const snackbar = useAlert();
  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  const { getFormikConfig, getListFormikConfig } = useProfileFormikConfigs();
  const { setEditKey } = useEditableSectionsContext();
  const { editIndex } = useEditableSectionListContext();
  const [ deleteDegreeId, setDeleteDegreeId ] = useState<string | null>(null);

  const deleteDegreeMutation = useMutation({
    mutationFn: deleteProviderQualificationDegree,
    onSuccess: async () => {
      await queryClient.invalidateQueries(PROFILE_QUERY_KEY(profile.user.id));
      snackbar.success('Degree deleted');
      setEditKey(null);
    },
    onError: async () => {
      snackbar.error('Could not delete degree');
    },
  });

  const onCloseDeleteModal = useCallback((shouldDelete: boolean) => {
    if (deleteDegreeId && shouldDelete) {
      deleteDegreeMutation.mutate(deleteDegreeId);
    }

    setDeleteDegreeId(null);
  }, [ deleteDegreeId, deleteDegreeMutation ]);

  return (
    <>
      {deleteDegreeId && (
        <DeleteResourceModal
          title="Delete Degree?"
          onClose={onCloseDeleteModal}
          cancelButtonText="Keep Degree"
        />
      )}
      <EditableSectionContainer
        title="Clinical experience"
        rowDividers
        formikConfigs={{
          [MatchingProfileRow.specialties]: getFormikConfig(MatchingProfileRow.specialties),
          [MatchingProfileRow.communityExpertise]: getFormikConfig(MatchingProfileRow.communityExpertise),
          [MatchingProfileRow.treatmentApproach]: getFormikConfig(MatchingProfileRow.treatmentApproach),
          [MatchingProfileRow.therapeuticStyle]: getFormikConfig(MatchingProfileRow.therapeuticStyle),
          [MatchingProfileRow.education]: getListFormikConfig(MatchingProfileRow.education, editIndex),
        }}
      >
        <RequiredProfileEditableSectionRow
          label={profileRowKeyToLabel.specialties}
          rowKey={MatchingProfileRow.specialties}
          form={<SpecialtiesForm />}
          kind="editable-row"
          editModalNoPadding
          editTip={(
            <EditableSectionRowTip
              title="Tip"
              description="Highlight your top specialties but don't be afraid to add more.<br /><br />Providers who add 5 or more specialties get more bookings: BetterHelp uses profile specialties to figure out which providers to show clients during matching.<br /><br />Common client searches include Anxiety, Depression, ADD/ADHD, and LGBTQIA+ identity."
            />
          )}
        >
          <Typography className={classes.fontWeight500}>
            {profile.specialties.map(e => e.name).join(', ')}
          </Typography>
        </RequiredProfileEditableSectionRow>
        <EditableSectionRow
          label={profileRowKeyToLabel.communityExpertise}
          rowKey={MatchingProfileRow.communityExpertise}
          form={<CommunityExpertiseForm />}
          kind="editable-row"
          editTip={(
            <EditableSectionRowTip
              title="What’s this used for?"
              description="Add any identities and communities where you have expertise or experience working in.<br /><br />Clients sometimes seek providers who understand their background or the life experiences of certain identities or communities."
            />
          )}
        >
          <Box display="flex" flexDirection="column" gap="1.5rem">
            <IconWithContent
              icon={<PublicOutlined />}
              primary="Ethnicity and Race"
              secondary={profile.communityExpertise.ethnicities.map(e => e.name).join(', ')}
              secondaryOptional
              alignIcon="flex-start"
            />
            <IconWithContent
              icon={<FoldedHandsIcon />}
              primary="Faith"
              secondary={profile.communityExpertise.faiths.map(e => e.name).join(', ')}
              secondaryOptional
              alignIcon="flex-start"
            />
            {/* PHASE 3 */}
            <IconWithContent
              icon={<GroupsIcon />}
              primary="General"
              secondary={profile.communityExpertise.general.map(e => e.name).join(', ')}
              secondaryOptional
              alignIcon="flex-start"
            />
          </Box>
        </EditableSectionRow>
        <RequiredProfileEditableSectionRow
          label={profileRowKeyToLabel.treatmentApproach}
          rowKey={MatchingProfileRow.treatmentApproach}
          form={<TreatmentApproachForm />}
          kind="editable-row"
          editModalNoPadding
          editTip={(
            <EditableSectionRowTip
              title="Tip"
              description="Add any other approaches you use in your treatment.  For example, many providers use some sort of Compassion Focused, Emotionally Focused, Mindfulness-Based, or Person-Centered therapy."
            />
          )}
        >
          <Typography className={classes.fontWeight500}>
            {profile.treatmentApproaches.map(getTreatmentApproachName).join(', ')}
          </Typography>
        </RequiredProfileEditableSectionRow>
        <EditableSectionRow
          label={profileRowKeyToLabel.therapeuticStyle}
          rowKey={MatchingProfileRow.therapeuticStyle}
          form={<TherapueticStyleForm />}
          kind="editable-row"
          editTip={(
            <EditableSectionRowTip
              title="Tip"
              description="Give clients a feel for how you structure sessions."
            />
          )}
        >
          <Typography className={classes.fontWeight500} component={OptionalTypography}>
            {profile.therapeuticStyles.map(e => e.name).join(', ')}
          </Typography>
        </EditableSectionRow>
        <RequiredProfileEditableSectionRowList
          label={profileRowKeyToLabel.education}
          rowKey={MatchingProfileRow.education}
          contentList={profile.qualification.degrees.map(({ school, fieldOfStudy, degree, yearGraduated, id }) => ({
            content: (
              <IconWithContent
                icon={<SchoolOutlined />}
                primary={`${degree} in ${fieldOfStudy}`}
                secondary={`${school}, ${yearGraduated}`}
              />
            ),
            id: id,
          }))}
          form={EducationForm}
          getEditLabel={index => `Degree ${index + 1}`}

          rowFormWrapperProps={{
            cancelButtonProps: { disabled: deleteDegreeMutation.isLoading },
            saveButtonProps: { disabled: deleteDegreeMutation.isLoading },
          }}
          addButtonConfig={{
            text: 'Add new degree'
          }}
          deleteButtonConfig={{
            onDelete: setDeleteDegreeId,
            disabled: profile.qualification.degrees.length === 1 || deleteDegreeMutation.isLoading,
            disabledTooltip: 'At least 1 degree is required',
            text: 'Delete degree',
          }}
        />
      </EditableSectionContainer>
    </>
  );
};
